// Falls back to a direct localhost:8000 backend for local dev. On EC2 (or any
// deployment behind the nginx reverse proxy), set REACT_APP_API_BASE=/api in
// frontend/.env so calls go through the proxy instead — see README.
const BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

async function req(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || "Request failed");
  }
  // 204 No Content returns no body
  if (res.status === 204) return null;
  return res.json();
}

function qs(params = {}) {
  const s = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null && v !== "")
  ).toString();
  return s ? "?" + s : "";
}

// ── Auth ──────────────────────────────────────────────────────────────────────
export const login = (email, password) =>
  req("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });

// ── Requests ──────────────────────────────────────────────────────────────────
export const getRequests   = (p = {}) => req(`/requests${qs(p)}`);
export const getRequest    = (id) => req(`/requests/${id}`);
export const createRequest = (data) => req("/requests/", { method: "POST", body: JSON.stringify(data) });
export const reviewRequest = (id, data) => req(`/requests/${id}/review`, { method: "POST", body: JSON.stringify(data) });
export const getAssignments = (id) => req(`/requests/${id}/assignments`);
export const manualAssign  = (id, data) => req(`/requests/${id}/assign`, { method: "POST", body: JSON.stringify(data) });
export const recordUsage   = (reqId, asgId, data) =>
  req(`/requests/${reqId}/assignments/${asgId}/usage`, { method: "PATCH", body: JSON.stringify(data) });

// ── Model registry ────────────────────────────────────────────────────────────
export const getModels   = (activeOnly = false) => req(`/models${activeOnly ? "?active_only=true" : ""}`);
export const createModel = (data) => req("/models/", { method: "POST", body: JSON.stringify(data) });
export const toggleModel = (id) => req(`/models/${id}/toggle`, { method: "PATCH" });

// ── Teams ─────────────────────────────────────────────────────────────────────
export const getTeams    = ()         => req("/teams/");
export const createTeam  = (data)     => req("/teams/", { method: "POST", body: JSON.stringify(data) });
export const updateTeam  = (id, data) => req(`/teams/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteTeam  = (id)       => req(`/teams/${id}`, { method: "DELETE" });

// ── API Key listing ───────────────────────────────────────────────────────────
export const getApiKeys       = (p = {}) => req(`/assignments/${qs(p)}`);
export const deleteApiKey     = (id)     => req(`/assignments/${id}`, { method: "DELETE" });
export const bedrockCostCheck = (id, p = {}) => req(`/assignments/cost-check/${id}${qs(p)}`);

// ── Metrics ───────────────────────────────────────────────────────────────────
export const getMetrics        = (p = {}) => req(`/metrics/${qs(p)}`);
export const getTeamMetrics    = (p = {}) => req(`/metrics/teams${qs(p)}`);
export const getUserMetrics    = (p = {}) => req(`/metrics/users${qs(p)}`);
export const getManagerMetrics = (email, p = {}) =>
  req(`/metrics/manager/${encodeURIComponent(email)}${qs(p)}`);
export const getAlerts         = ()       => req("/metrics/alerts");
export const getTimeline       = (p = {}) => req(`/metrics/timeline${qs(p)}`);

// ── Credit requests ───────────────────────────────────────────────────────────
export const getEmployees            = ()         => req("/credit-requests/employees");
export const getCreditRequests       = (p = {})   => req(`/credit-requests/${qs(p)}`);
export const getCreditRequest        = (id)        => req(`/credit-requests/${id}`);
export const getCreditMetrics        = (p = {})    => req(`/credit-requests/metrics${qs(p)}`);
export const getCreditRequestImpact   = (id)        => req(`/credit-requests/${id}/impact`);
export const createCreditRequest     = (data)      => req("/credit-requests/", { method: "POST", body: JSON.stringify(data) });
export const managerReviewCredit     = (id, data)  => req(`/credit-requests/${id}/manager-review`, { method: "POST", body: JSON.stringify(data) });
export const adminReviewCredit       = (id, data)  => req(`/credit-requests/${id}/admin-review`,   { method: "POST", body: JSON.stringify(data) });

// ── Usage & Results dashboard ─────────────────────────────────────────────────
export const getUsageSummary = ()      => req("/usage-dashboard/summary");
export const getUsageHeatmap = (p = {}) => req(`/usage-dashboard/heatmap${qs(p)}`);
export const getUsageTeams   = (p = {}) => req(`/usage-dashboard/teams${qs(p)}`);
export const refreshUsageDashboard = () => req("/usage-dashboard/refresh", { method: "POST" });
